import z from "zod";

import { BANNER_LINK_TYPES } from "@/types";
import {
  BannerTarget,
  SCREEN_TARGET_KINDS,
  isHttpUrl,
} from "@/utils/banner-link";

const PROMO_CODE_SCHEMA = z.string().trim();

const BANNER_TARGET_SCHEMA: z.ZodType<BannerTarget> = z
  .discriminatedUnion("kind", [
    z.object({
      kind: z.literal("product"),
      productId: z.string().min(1, "Choose a product"),
      promoCode: PROMO_CODE_SCHEMA,
    }),
    z.object({
      kind: z.literal("products"),
      categoryId: z.string(),
      subCategoryId: z.string(),
      brandId: z.string(),
      countryId: z.string(),
      promoCode: PROMO_CODE_SCHEMA,
    }),
    z.object({
      kind: z.literal("search"),
      searchTerm: z.string().trim().min(1, "Enter a search term"),
      promoCode: PROMO_CODE_SCHEMA,
    }),
    z.object({
      kind: z.enum(SCREEN_TARGET_KINDS),
      promoCode: PROMO_CODE_SCHEMA,
    }),
    z.object({
      kind: z.literal("website"),
      url: z
        .string()
        .trim()
        .min(1, "Enter a website URL")
        .refine(
          isHttpUrl,
          "Enter a full URL starting with http:// or https://",
        ),
    }),
    z.object({
      kind: z.literal("custom"),
      link: z.string().trim().min(1, "Link is required"),
      linkType: z.enum(BANNER_LINK_TYPES),
    }),
  ])
  .superRefine((target, ctx) => {
    if (
      target.kind === "custom" &&
      target.linkType === "web-link" &&
      !z.string().url().safeParse(target.link).success
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Web links must be a valid URL",
        path: ["link"],
      });
    }
  });

export const BANNER_FORM_SCHEMA = z.object({
  imageUrl: z.string().min(1, "Upload a banner image"),
  target: BANNER_TARGET_SCHEMA,
});

export type BannerFormData = z.infer<typeof BANNER_FORM_SCHEMA>;

export const EMPTY_BANNER_FORM: BannerFormData = {
  imageUrl: "",
  target: {
    kind: "products",
    categoryId: "",
    subCategoryId: "",
    brandId: "",
    countryId: "",
    promoCode: "",
  },
};
